import React, { useState, useEffect, useRef } from "react";
import "./BulkAddProductForm.css";

// Delivery Order regex validation
const DO_RE = /^\d{6,10}$/;
const IMEI_RE = /^3[0-9]{14}$/;      // 15 digits, must start with 3
const SERIAL_RE = /^R[0-9A-Za-z]{10}$/;    // 11 chars, must start with R followed by 10 alphanumeric characters

const cleanScan = (v) => (v ?? "").replace(/[\r\n\t]/g, "").trim();

export default function BulkAddProductForm({ onClose, onBulkAddComplete, userId }) {
  const [form, setForm] = useState({
    asset1_id: "",
    asset2_id: "",
    inventory_type: "",
    delivery_order_number: "",
    location_id: "",
    partner_id: "",
    include_imei: false,
  });

  const [models, setModels] = useState([]);
  const [colors, setColors] = useState([]);
  const [locations, setLocations] = useState([]);
  const [partners, setPartners] = useState([]);
  const [rows, setRows] = useState([{ imei_number: "", serial_number: "" }]);
  const [rowErrors, setRowErrors] = useState({});
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const imeiRefs = useRef([]);
  const serialRefs = useRef([]);
  const scrollRef = useRef(null);

  /* ---------------- Load models + locations + partners ---------------- */
  useEffect(() => {
    fetch("/api/models").then(r => r.json()).then(setModels);
    fetch("/api/locations").then(r => r.json()).then(setLocations);
    fetch("/api/partners").then(r => r.json()).then(setPartners);
  }, []);

  /* ---------------- Load colors ---------------- */
  useEffect(() => {
    if (!form.asset1_id) {
      setColors([]);
      return;
    }
    fetch(`/api/models/${form.asset1_id}/colors`)
      .then(r => r.json())
      .then(setColors);
  }, [form.asset1_id]);

  /* ---------------- Form updates ---------------- */
  const updateForm = (e) => {
    const { name, type, checked, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  /* ---------------- Row-level format checks ---------------- */
  // Returns { imeiBad, serialBad } for a given row, using the row's own
  // (already-cleaned) values. "Bad" only fires once something's been entered.
  const rowFormatIssues = (row) => {
    const imei = cleanScan(row.imei_number);
    const serial = cleanScan(row.serial_number);
    const imeiBad = form.include_imei && imei.length > 0 && !IMEI_RE.test(imei);
    const serialBad = serial.length > 0 && !SERIAL_RE.test(serial);
    return { imeiBad, serialBad };
  };

  // True if ANY row currently has a bad IMEI or Serial — used to disable submit.
  const hasFormatErrors = rows.some(r => {
    const { imeiBad, serialBad } = rowFormatIssues(r);
    return imeiBad || serialBad;
  });

  /* ---------------- Row helpers ---------------- */
  const isDuplicate = (field, value, index) =>
    rows.some((r, i) => i !== index && r[field] === value);

  const updateRow = (index, field, rawValue, final = false) => {
    const value = final ? cleanScan(rawValue) : rawValue;
    const newRows = [...rows];
    newRows[index][field] = value;

    if (field === "imei_number" && /^\d{15}$/.test(value)) {
      setForm(f => ({ ...f, include_imei: true }));
    }

    if (final && value && isDuplicate(field, value, index)) {
      setRowErrors(prev => ({ ...prev, [index]: true }));
      setRows(newRows);
      return;
    }

    // Also flag format problems immediately on a finalized scan so the row
    // turns red right away, not just on submit.
    const { imeiBad, serialBad } = rowFormatIssues(newRows[index]);
    setRowErrors(prev => ({ ...prev, [index]: final ? (imeiBad || serialBad) : prev[index] }));
    setRows(newRows);

    if (!final) return;

    // Don't auto-advance focus / add a new row on a bad scan — force the
    // user to see and fix it first.
    if (imeiBad || serialBad) return;

    const isLast = index === rows.length - 1;

    if (field === "imei_number") {
      setTimeout(() => serialRefs.current[index]?.focus(), 20);
      return;
    }

    if (field === "serial_number") {
      if (isLast) {
        setRows(prev => [...prev, { imei_number: "", serial_number: "" }]);
        setTimeout(() => {
          scrollRef.current?.scrollTo({
            top: scrollRef.current.scrollHeight,
            behavior: "smooth",
          });
          form.include_imei
            ? imeiRefs.current[index + 1]?.focus()
            : serialRefs.current[index + 1]?.focus();
        }, 40);
      } else {
        setTimeout(() => {
          form.include_imei
            ? imeiRefs.current[index + 1]?.focus()
            : serialRefs.current[index + 1]?.focus();
        }, 20);
      }
    }
  };

  const handleScanEnter = (index, field) => (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const value = cleanScan(e.target.value);
    if (value) updateRow(index, field, value, true);
  };

  const removeRow = (index) => {
    setRows(prev => prev.filter((_, i) => i !== index));
    setRowErrors(prev => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const addRow = () => {
    setRows(prev => [...prev, { imei_number: "", serial_number: "" }]);
    setTimeout(() => {
      scrollRef.current?.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: "smooth",
      });
    }, 40);
  };

  /* ---------------- Validation ---------------- */
  const validate = () => {
    if (!form.asset1_id) return "Please select a model.";
    if (!form.asset2_id) return "Please select a colour.";
    if (!form.inventory_type) return "Type is required.";
    if (!form.partner_id) return "Partner is required.";
    if (!form.location_id) return "Location is required.";
    if (form.delivery_order_number && !DO_RE.test(form.delivery_order_number))
      return "Delivery Order must be 6–10 digits.";

    const errors = {};
    let hasAny = false;
    let hasFormatIssue = false;

    rows.forEach((r, i) => {
      const imei = cleanScan(r.imei_number);
      const serial = cleanScan(r.serial_number);
      if (!imei && !serial) return;
      hasAny = true;

      const { imeiBad, serialBad } = rowFormatIssues(r);

      if (!serial || (form.include_imei && !imei)) {
        errors[i] = true;
        return;
      }
      if (imeiBad || serialBad) {
        errors[i] = true;
        hasFormatIssue = true;
      }
    });

    if (!hasAny) return "Please enter at least one item.";

    setRowErrors(errors);
    if (hasFormatIssue) {
      return 'One or more rows have an invalid IMEI (15 digits, starts with "3") or Serial Number (11 characters, starts with "R").';
    }
    if (Object.keys(errors).length) return "Some rows are incomplete.";

    return "";
  };

  /* ---------------- Submit ---------------- */
  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");

    const msg = validate();
    if (msg) return setError(msg);

    const payload = {
      asset1_id: Number(form.asset1_id),
      asset2_id: Number(form.asset2_id),
      inventory_type: form.inventory_type,
      delivery_order_number: form.delivery_order_number || null,
      location_id: Number(form.location_id),
      partner_id: Number(form.partner_id), 
      user_id: userId,
      items: rows
        .filter(r => cleanScan(r.serial_number))
        .map(r => ({
          imei_number: form.include_imei ? cleanScan(r.imei_number) : null,
          serial_number: cleanScan(r.serial_number),
        })),
    };

    setSubmitting(true);

    fetch("/api/products/bulk", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then(async r => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.error || "Bulk add failed");
      })
      .then(() => {
        onBulkAddComplete?.();
        onClose();
        window.location.reload(); //Refresh page 
      })
      .catch(err => setError(err.message))
      .finally(() => setSubmitting(false));
  };

  /* ---------------- UI ---------------- */
  return (
    <div className="modal-overlay">
      <div className="modal-content big-modal">
        <button className="close-button" onClick={onClose}>✖</button>

        <div className="modal-header">
          <h3>Bulk Add Products</h3>

          <label className="imei-toggle">
            <input
              type="checkbox"
              name="include_imei"
              checked={form.include_imei}
              onChange={updateForm}
            />
            Include IMEI Number
          </label>
        </div>

        {error && <p className="error-text">{error}</p>}

        <form onSubmit={handleSubmit} className="bulk-form-layout">

          {/* LEFT COLUMN */}
          <div className="bulk-form-left">

            <div className="form-group">
              <label>Model:</label>
              <select name="asset1_id" value={form.asset1_id} onChange={updateForm}>
                <option value="">Select model</option>
                {models.map((m, i) => (
                  <option key={`model-${m.id ?? m.asset1_id ?? i}`} value={m.id ?? m.asset1_id}>
                    {m.name ?? m.asset1_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Colour:</label>
              <select
                name="asset2_id"
                value={form.asset2_id}
                onChange={updateForm}
                disabled={!form.asset1_id}
              >
                <option value="">Select colour</option>
                {colors.map((c, i) => (
                  <option key={`colour-${c.id ?? c.asset2_id ?? i}`} value={c.id ?? c.asset2_id}>
                    {c.name ?? c.asset2_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Type:</label>
              <select
                name="inventory_type"
                value={form.inventory_type}
                onChange={updateForm}
              >
                <option value="">Select type</option>
                <option value="FOC">FOC</option>
                <option value="PO">PO</option>
              </select>
            </div>

            <div className="form-group">
              <label>Delivery Order No.:</label>
              <input
                type="text"
                name="delivery_order_number"
                value={form.delivery_order_number}
                onChange={updateForm}
                maxLength={10}
              />
            </div>

            <div className="form-group">
              <label>Partner:</label>
              <select
                name="partner_id"
                value={form.partner_id}
                onChange={updateForm}
              >
                <option value="">Select Partner</option>
                {partners.map((p, i) => (
                  <option key={`partner-${p.id ?? i}`} value={p.id}>
                  {p.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Location:</label>
              <select
                name="location_id"
                value={form.location_id}
                onChange={updateForm}
              >
                <option value="">Select location</option>
                {locations.map((l, i) => (
                  <option key={`location-${l.location_id ?? i}`} value={l.location_id}>
                    {l.location_name}
                  </option>
                ))}
              </select>
            </div>

          </div>

          {/* RIGHT COLUMN */}
          <div className="bulk-form-right">
            <h4>Scanned Items</h4>

            <button
              type="button"
              className="add-row-btn"
              onClick={addRow}
            >
              + Add Row
            </button>

            <div className="bulk-form-right-inner" ref={scrollRef}>
              <div className="bulk-table-container">
                <table>
                  <thead>
                    <tr>
                      <th>#</th>
                      {form.include_imei && <th>IMEI</th>}
                      <th>Serial</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, idx) => {
                      const { imeiBad, serialBad } = rowFormatIssues(row);
                      return (
                        <tr key={idx} className={rowErrors[idx] ? "row-error" : ""}>
                          <td>{idx + 1}</td>

                          {form.include_imei && (
                            <td>
                              <input
                                ref={el => (imeiRefs.current[idx] = el)}
                                className={imeiBad ? "cell-error" : ""}
                                value={row.imei_number}
                                onChange={e =>
                                  updateRow(idx, "imei_number", e.target.value)
                                }
                                onKeyDown={handleScanEnter(idx, "imei_number")}
                                maxLength={15}
                                title={imeiBad ? 'IMEI must be 15 digits and start with "3".' : undefined}
                              />
                            </td>
                          )}

                          <td>
                            <input
                              ref={el => (serialRefs.current[idx] = el)}
                              className={serialBad ? "cell-error" : ""}
                              value={row.serial_number}
                              onChange={e =>
                                updateRow(idx, "serial_number", e.target.value)
                              }
                              onKeyDown={handleScanEnter(idx, "serial_number")}
                              maxLength={11}
                              title={serialBad ? 'Serial Number must be 11 characters, starting with "R" followed by 10 digits.' : undefined}
                            />
                          </td>

                          <td>
                            {idx < rows.length - 1 && (
                              <button
                                type="button"
                                className="row-delete-btn"
                                onClick={() => removeRow(idx)}
                              >
                                ✖
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

        </form>

        <button
          type="submit"
          className="submit-btn"
          disabled={submitting || hasFormatErrors}
          onClick={handleSubmit}
        >
          {submitting ? "Adding…" : "Add All Products"}
        </button>

      </div>
    </div>
  );
}